import React, { useState, useContext, useEffect } from 'react';
import { FieldData, JobWorkflowDefaultTasksRowsFieldType } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import { v4 as uuidv4 } from 'uuid';
import FormFieldContext from '../FormFieldContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen, faGripLines, faTrash, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { JobWorkflowDefaultTaskType, createNewJobWorkflowDefaultTask } from '../../../views/settings/jobs/JobTypeDefaultsTypes';
import { FormType } from '../../../views/forms/FormTypes';
import Dropdown from '../basefields/Dropdown';
import { renderRowsFieldErrorMessages } from './functions/renderRowsFieldErrorMessages';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

const JobWorkflowDefaultTasksRowsField: React.FC<JobWorkflowDefaultTasksRowsFieldType & { data: FieldData, viewKey: string }> = ({
    name, label, tooltipText, data, dropdownData
}) => {
    const { t } = useTranslation();
    const { errorMessages } = useGlobalContext();
    const { editing, setUpdatedData, showErrorAlert } = useContext(FormFieldContext);
    const { activeFeatures } = useAuthContext();
    const [rows, setRows] = useState<JobWorkflowDefaultTaskType[]>([]);
    const [forms, setForms] = useState<FormType[]>([]);

    // Only show the form dropdown when the forms module is active
    const formsEnabled = activeFeatures?.includes('forms_base');

    // If there are default tasks in the fetched data, show them. Otherwise show an empty row
    useEffect(() => {
        if (data && data[name] && data[name].length > 0) {
            const sortedRows = (data[name] || []).sort(
                (a: JobWorkflowDefaultTaskType, b: JobWorkflowDefaultTaskType) => (a.ordering ?? 0) - (b.ordering ?? 0)
            ).map((row: JobWorkflowDefaultTaskType) => ({ ...row }));
            setRows(sortedRows);
        } else {
            const firstPosition = 1;
            setRows([createNewJobWorkflowDefaultTask(uuidv4(), firstPosition)]);
        }
    }, [data]);

    // Set the forms from the dropdown data
    useEffect(() => {
        setForms(dropdownData?.form?.results || []);
    }, [dropdownData]);

    // Update the rows state and updated data
    const syncUpdatedData = (updatedRows: JobWorkflowDefaultTaskType[]) => {
        setRows(updatedRows);
        setUpdatedData((current: any) => ({ ...current, [name]: updatedRows }));
    };

    // Update a row
    const updateRow = (identifier: string, updater: (row: JobWorkflowDefaultTaskType) => JobWorkflowDefaultTaskType) => {
        const updatedRows = rows.map(row =>
            row.id?.toString() === identifier || row.uuid === identifier ? updater(row) : row
        );
        syncUpdatedData(updatedRows);
    };

    // Handle the creation of a new row
    const handleAddRow = () => {
        // Split rows in non deleted and deleted rows
        const nonDeletedRows = rows.filter(row => !row.deleted);
        const deletedRows = rows.filter(row => row.deleted);

        // Determine the ordering of the new row and create it
        const newOrdering = nonDeletedRows.length + 1;
        const newRow = createNewJobWorkflowDefaultTask(uuidv4(), newOrdering);

        // Add the new row at the end of the list with non deleted rows and add the deleted rows
        const updatedRows = [...nonDeletedRows, newRow, ...deletedRows];

        syncUpdatedData(updatedRows);
    };

    // Handle deletion of a row
    const handleDeleteRow = (identifier: string) => {
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Mark the row as deleted without actually deleting it
            updatedRows[rowIndex].deleted = true;

            // Update the ordering of the rows which are not marked as deleted
            const nonDeletedRows = updatedRows.filter(row => !row.deleted);
            const reorderedNonDeletedRows = nonDeletedRows.map((row, index) => ({
                ...row,
                ordering: index + 1,
            }));

            // Add the deleted rows to the reordered list to keep them for the updated data
            const finalRows = [...reorderedNonDeletedRows, ...updatedRows.filter(row => row.deleted)];

            syncUpdatedData(finalRows);
        }
    };

    // Handle ordering change
    const handleOrderingChange = (result: any) => {
        if (!result.destination) return;
        const { source, destination } = result;

        // Only use the non deleted rows to determine the new ordering
        const nonDeletedRows = rows.filter(row => !row.deleted);
        const [reorderedRow] = nonDeletedRows.splice(source.index, 1);
        nonDeletedRows.splice(destination.index, 0, reorderedRow);

        // Determine the ordering for the non deleted rows
        const updatedNonDeletedRows = nonDeletedRows.map((row, index) => ({
            ...row,
            ordering: index + 1,
        }));

        // Add the deleted rows to the list without changing their ordering
        const finalRows = [...updatedNonDeletedRows, ...rows.filter(row => row.deleted)];

        syncUpdatedData(finalRows);
    };

    // Handle form change
    const handleFormChange = (identifier: string, value: string) => {
        const formId = value === 'none' ? null : Number(value);
        updateRow(identifier, row => ({ ...row, form: formId }));
    };

    return (
        <div className='rows-field job-type-defaults-field'>
            {label &&
                <label>
                    {t(label)}
                    {tooltipText &&
                        <span className="tooltip-icon">
                            <FontAwesomeIcon icon={faCircleQuestion} />
                            <span className="tooltip tooltip-right">{t(tooltipText)}</span>
                        </span>
                    }
                </label>
            }
            {showErrorAlert &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            {editing && (
                <div className='edit-mode'>
                    <DragDropContext onDragEnd={handleOrderingChange}>
                        <StrictModeDroppable droppableId="droppable">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {rows && rows.filter(row => row.deleted === false).map((row, index) => {
                                        const identifier = row.id?.toString() || row.uuid || '';
                                        return (
                                            <Draggable key={identifier} draggableId={`default-task-${identifier}`} index={index} isDragDisabled={rows.length <= 1}>
                                                {(provided) => (
                                                    <div className='list-item draggable' ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                        <div className={`default-task-row ${formsEnabled ? 'forms-enabled' : ''}`}>
                                                            <input
                                                                type='text'
                                                                id={`description_${identifier}`}
                                                                name={`description_${identifier}`}
                                                                value={row.description}
                                                                placeholder={t('job.job_type.default_tasks.description_placeholder')}
                                                                onChange={event => updateRow(identifier, r => ({ ...r, description: event.target.value }))}
                                                                className={identifier && errorMessages?.default_tasks?.[identifier]?.['description'] ? 'is-invalid' : undefined}
                                                            />
                                                            {formsEnabled && (
                                                                <div className='task-form-selector'>
                                                                    <Dropdown<FormType>
                                                                        name={`default_task_form_${identifier}`}
                                                                        options={forms}
                                                                        selectedOption={forms.find(form => form.id === row.form) ?? null}
                                                                        backendField='id'
                                                                        optionFormat='name'
                                                                        disabled_selected='job.tasks.select_form_disabled_selected'
                                                                        selectionFormat={(option) => option.name}
                                                                        allowNoneOption={true}
                                                                        noneOptionLabel='forms.none_option'
                                                                        showSearch={true}
                                                                        onChange={(value) => handleFormChange(identifier, value)}
                                                                    />
                                                                </div>
                                                            )}
                                                            <div
                                                                className={`task-required-icon ${row.required ? 'active' : ''} tooltip-icon`}
                                                                onClick={() => updateRow(identifier, r => ({ ...r, required: !r.required }))}
                                                            >
                                                                {row.required
                                                                    ? <FontAwesomeIcon icon={faLock} />
                                                                    : <FontAwesomeIcon icon={faLockOpen} />
                                                                }
                                                                <span className="tooltip">{t('job.tasks.task_required_tooltip')}</span>
                                                            </div>
                                                            <div className='delete-icon tooltip-icon'>
                                                                <FontAwesomeIcon icon={faTrash} onClick={() => handleDeleteRow(identifier)} />
                                                                <span className="tooltip">{t('general.delete')}</span>
                                                            </div>
                                                            <div className={`order-icon tooltip-icon ${rows.length === 1 ? 'visibility-hidden' : ''}`}>
                                                                <FontAwesomeIcon icon={faGripLines} />
                                                                <span className="tooltip">{t('general.reorder')}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>
                    {errorMessages &&
                        <div className='error-message'>
                            {renderRowsFieldErrorMessages(errorMessages, 'default_tasks', t)}
                        </div>
                    }
                    <div onClick={(e) => { e.preventDefault(); handleAddRow(); }}
                        className="add-new-button">
                        {t('job.job_type.default_tasks.add_new_item')}
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobWorkflowDefaultTasksRowsField;
